import React, { forwardRef, useImperativeHandle } from 'react';
import useEmailFieldValidation from '../../hooks/useEmailFieldValidation';
import { getEmailValidationInputClass } from '../../utils/emailValidation';

const EmailInput = forwardRef(
  (
    {
      value,
      onChange,
      onBlur,
      name = 'email',
      placeholder,
      className = '',
      requiredMessage,
      invalidMessage,
      extraValidator,
      showSuccessMessage = true,
      defaultBorderClass = 'border-jet-gray-300 focus:ring-jet-blue',
      wrapperClassName = '',
      ...inputProps
    },
    ref
  ) => {
    const {
      emailTouched,
      emailError,
      onEmailChange,
      onEmailBlur,
      validateEmailForSubmit,
      resetEmailValidation,
    } = useEmailFieldValidation({ requiredMessage, invalidMessage, extraValidator });

    useImperativeHandle(ref, () => ({
      validate: () => validateEmailForSubmit(value),
      reset: resetEmailValidation,
      error: emailError,
      touched: emailTouched,
    }));

    const handleChange = (e) => {
      onEmailChange(e.target.value);
      onChange?.(e);
    };

    const handleBlur = (e) => {
      onEmailBlur(e.target.value);
      onBlur?.(e);
    };

    const hasError = emailTouched && emailError;
    const isValid = emailTouched && !emailError && value?.trim();

    const inputClassName = getEmailValidationInputClass(className, {
      touched: emailTouched,
      error: emailError,
      value,
      defaultBorderClass,
    });

    return (
      <div className={wrapperClassName}>
        <input
          type="email"
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={inputClassName}
          {...inputProps}
        />
        {hasError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
        {showSuccessMessage && isValid && (
          <p className="text-green-600 text-sm mt-1">✓ Email is valid</p>
        )}
      </div>
    );
  }
);

EmailInput.displayName = 'EmailInput';

export default EmailInput;
